import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTimes, FaStar } from 'react-icons/fa';
import './Services.css';

const Membership = () => {
  const plans = [
    {
      name: 'Basic',
      price: '$49',
      period: 'per month',
      description: 'Perfect for casual players who play occasionally',
      features: [
        { text: '4 court hours per month', included: true },
        { text: '10% discount on additional bookings', included: true },
        { text: '5% discount at pro shop', included: true },
        { text: 'Access to member events', included: true },
        { text: 'Guest passes', included: false },
        { text: 'Priority booking', included: false },
        { text: 'Free equipment rental', included: false },
        { text: 'Personal locker', included: false }
      ],
      popular: false
    },
    {
      name: 'Premium',
      price: '$99',
      period: 'per month',
      description: 'Our most popular plan for regular players',
      features: [
        { text: '12 court hours per month', included: true },
        { text: '20% discount on additional bookings', included: true },
        { text: '15% discount at pro shop', included: true },
        { text: 'Access to member events', included: true },
        { text: '2 guest passes per month', included: true },
        { text: 'Priority booking (48hr advance)', included: true },
        { text: 'Free equipment rental', included: false },
        { text: 'Personal locker', included: false }
      ],
      popular: true
    },
    {
      name: 'Elite',
      price: '$179',
      period: 'per month',
      description: 'Unlimited access for serious badminton enthusiasts',
      features: [
        { text: 'Unlimited court hours', included: true },
        { text: '30% discount on training programs', included: true },
        { text: '20% discount at pro shop', included: true },
        { text: 'VIP access to all events', included: true },
        { text: '4 guest passes per month', included: true },
        { text: 'Priority booking (72hr advance)', included: true },
        { text: 'Free equipment rental', included: true },
        { text: 'Personal locker included', included: true }
      ],
      popular: false
    }
  ];

  const annualPlans = [
    { name: 'Basic Annual', price: '$470', savings: 'Save $118', originalPrice: '$588' },
    { name: 'Premium Annual', price: '$950', savings: 'Save $238', originalPrice: '$1,188' },
    { name: 'Elite Annual', price: '$1,719', savings: 'Save $429', originalPrice: '$2,148' }
  ];

  const benefits = [
    {
      title: 'Priority Booking',
      description: 'Get first access to court reservations before non-members'
    },
    {
      title: 'Member Discounts',
      description: 'Enjoy exclusive discounts on training programs, equipment, and events'
    },
    {
      title: 'Community Access',
      description: 'Join our vibrant community with exclusive member events and tournaments'
    },
    {
      title: 'Flexible Terms',
      description: 'Choose monthly or annual plans with the option to upgrade anytime'
    }
  ];

  const faqs = [
    {
      question: 'Can I upgrade my membership?',
      answer: 'Yes, you can upgrade your membership at any time. The price difference will be prorated for the remaining period.'
    },
    {
      question: 'Are there any joining fees?',
      answer: 'New members pay a one-time registration fee of $50. This is waived for annual memberships.'
    },
    {
      question: 'Can I pause my membership?',
      answer: 'Premium and Elite members can pause their membership for up to 2 months per year with advance notice.'
    },
    {
      question: 'Do unused hours roll over?',
      answer: 'Court hours do not roll over to the next month. We encourage you to use your allocated hours each month.'
    },
    {
      question: 'Is there a family discount?',
      answer: 'Yes! Family members living at the same address receive 15% off their membership when signing up together.'
    }
  ];

  return (
    <div className="membership-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header-overlay"></div>
        <div className="container">
          <div className="page-header-content">
            <span className="page-badge">Membership</span>
            <h1>Join Our Club</h1>
            <p>Flexible membership plans designed for every player</p>
            <nav className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/services">Services</Link>
              <span>/</span>
              <span>Membership</span>
            </nav>
          </div>
        </div>
      </section>

      {/* Benefits Overview */}
      <section className="section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Member Benefits</span>
            <h2>Why Become a Member?</h2>
            <p>Unlock exclusive perks and savings with a Net Nest membership</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div className="benefit-card" key={index}>
                <FaStar className="benefit-icon" />
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section pricing-section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Monthly Plans</span>
            <h2>Choose Your Plan</h2>
            <p>Select the membership that fits your playing style</p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div className={`pricing-card ${plan.popular ? 'featured' : ''}`} key={index}>
                {plan.popular && <span className="pricing-badge">Most Popular</span>}
                <h3>{plan.name}</h3>
                <div className="price">{plan.price}<span>/{plan.period.split(' ')[1]}</span></div>
                <p className="period">{plan.description}</p>
                <ul className="pricing-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={!feature.included ? 'not-included' : ''}>
                      {feature.included ? <FaCheckCircle /> : <FaTimes />}
                      {feature.text}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className={`btn ${plan.popular ? 'btn-white' : 'btn-primary'} btn-block`}>
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Annual Plans */}
      <section className="section annual-section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Save More</span>
            <h2>Annual Memberships</h2>
            <p>Commit to a year and save up to 20%</p>
          </div>
          <div className="annual-grid">
            {annualPlans.map((plan, index) => (
              <div className="annual-card" key={index}>
                <h4>{plan.name}</h4>
                <div className="annual-price">
                  <span className="original">{plan.originalPrice}</span>
                  <span className="current">{plan.price}</span>
                </div>
                <span className="savings">{plan.savings}</span>
                <Link to="/contact" className="btn btn-outline-dark btn-sm">Select Plan</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section faq-section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Questions</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div className="faq-card" key={index}>
                <h4>{faq.question}</h4>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Join?</h2>
            <p>Become a member today and start enjoying exclusive benefits</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-white">Sign Up Now</Link>
              <Link to="/contact" className="btn btn-outline-white">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Membership;
